import React, {
    type FC,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type PropsWithChildren,
} from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

export const MessageList: FC<PropsWithChildren<Record<never, never>>> = ({ children }) => {
    const scrollDivRef = useRef<HTMLDivElement | null>(null);
    const bottomDivRef = useRef<HTMLDivElement | null>(null);
    const [showDropDownIcon, setShowDropDownIcon] = useState(false);

    useLayoutEffect(() => {
        if (scrollDivRef.current) {
            // set scroll to bottom of page when first renders
            scrollDivRef.current.scrollTop = scrollDivRef.current.scrollHeight;
        }
    }, []);

    useEffect(() => {
        if (bottomDivRef.current) {
            const observer = new IntersectionObserver(([entry]) => {
                // show dropdown icon when not at the bottom of the page
                setShowDropDownIcon(!entry.isIntersecting);
            });

            observer.observe(bottomDivRef.current);

            return () => observer.disconnect();
        }

        return undefined;
    }, []);

    const scrollToBottom = useCallback(() => {
        if (scrollDivRef.current) {
            scrollDivRef.current.scrollTo({
                top: scrollDivRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, []);

    return (
        <Box xcss={containerStyles}>
            <Box xcss={messagesContainerStyles} ref={scrollDivRef}>
                <Stack space="space.300" grow="fill">
                    {children}
                </Stack>
                <Box xcss={intersectionObserverStyles} ref={bottomDivRef} />
            </Box>
            <Box xcss={iconStyles} onClick={scrollToBottom}>
                {showDropDownIcon && (
                    <StyledButton
                        iconBefore={
                            <ChevronDownIcon primaryColor={token('color.text.accent.gray', colors.N60)} label="" />
                        }
                    />
                )}
            </Box>
        </Box>
    );
};

const StyledButton = styled(Button)({
    '&&': {
        border: `2px solid ${token('color.border.accent.gray', colors.N60)}`,
        borderRadius: '100%',
        height: '30px',
        width: '30px',
        background: 'none',
        padding: 0,
        justifyContent: 'center',
    },
});

const containerStyles = xcss({
    display: 'flex',
    gap: 'space.100',
    // To account for scroll to bottom icon on the right
    marginRight: '-38px',
});

const iconStyles = xcss({
    position: 'sticky',
    bottom: 'space.300',
    alignSelf: 'flex-end',
    width: '30px',
    height: '30px',
});

const messagesContainerStyles = xcss({
    overflowY: 'auto',
    paddingBlock: 'space.300',
    position: 'relative',
    flex: 1,
});

const intersectionObserverStyles = xcss({
    height: '0',
});
